import React from 'react';
import { lightTheme, lightScopedStyles } from './lightTheme';
import { darkTheme, darkScopedStyles } from './darkTheme';

import { tetTheme, tetScopedStyles } from './tetTheme';
import { noelTheme, noelScopedStyles } from './noelTheme';
import { cultureDayTheme, cultureDayScopedStyles } from './cultureDayTheme';
import { womenDayTheme, womenDayScopedStyles } from './womenDayTheme';
import { liberationTheme, liberationScopedStyles } from './liberationTheme';
import { midAutumnTheme, midAutumnScopedStyles } from './midAutumnTheme';
import { nationalDayTheme, nationalDayScopedStyles } from './nationalDayTheme';
import { buildThemeSegmentedScopedStyles } from './utils';
import vnFlag from 'assets/themes/0209/vn-flag.png';
import cultureDayIcon from 'assets/themes/cultureDay/header_left.svg';

const VnFlagLabel = ({ text }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <img
            src={vnFlag}
            alt=""
            width={16}
            height={11}
            style={{ objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
        />
        {text}
    </span>
);

const CultureDayLabel = ({ text }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <img
            src={cultureDayIcon}
            alt=""           
            width={18}
            height={20}
            style={{ objectFit: 'contain', flexShrink: 0 }}
        />
        {text}
    </span>
);

export const THEMES = {
    light: lightTheme,
    dark: darkTheme,
    nagaco: lightTheme,
    nationalDay: nationalDayTheme,
    tet: tetTheme,
    noel: noelTheme,
    womenDay: womenDayTheme,  
    liberation: liberationTheme,
    midAutumn: midAutumnTheme,
    cultureDay: cultureDayTheme,
};

export const THEME_OPTIONS = [
    { value: 'light', label: '☀️ Nagaco' },
    // { value: 'nagaco', label: '☀️ Nagaco' },
    { value: 'dark', label: '🌙 Dark' },   
    // { value: 'tet', label: '🧧 Tết' },
    // { value: 'noel', label: '🎄 Noel' },
    { value: 'nationalDay', label: <VnFlagLabel text="02/9" /> },
    { value: 'womenDay', label: '🌹 20/10' },
    { value: 'cultureDay', label: <CultureDayLabel text="24/11" /> },
   
    // { value: 'midAutumn', label: '🏮 Trung Thu' }, 
    // { value: 'liberation', label: '🇻🇳 30/4 - 1/5' },
      
];

/** light/dark CSS + Redux side-nav: chỉ 'dark' là dark, còn lại dùng light */
export const getCssThemeMode = (themeKey) => {
    switch (themeKey) {
        case 'dark':       
            return 'dark';

        default:
            return 'light';
    }
};

/** Màu header nav mặc định theo theme; ưu tiên token.headerNavColor */
export const getThemeHeaderNavBg = (themeKey, token) => {
    if (token?.headerNavColor) {
        return token.headerNavColor;
    }
    if (themeKey === 'light' || themeKey === 'dark') {
        return null;
    }
    return token?.moduleMenuBg ?? token?.colorPrimary ?? null;
};

/** Màu nền Layout (body) theo theme — khớp components.Layout.bodyBg của từng theme */
export const getThemeLayoutBodyBg = (themeKey, token) => {
    const layoutBodyBg = resolveThemeConfig(themeKey)?.components?.Layout?.bodyBg;
    if (layoutBodyBg) {
        return layoutBodyBg;
    }
    return token?.colorBgLayout ?? token?.bodyBg ?? token?.colorBgBase;
};

/** Màu nền header Table theo theme — khớp components.Table.headerBg */
export const getThemeTableHeaderBg = (themeKey, token) => {
    const headerBg = resolveThemeConfig(themeKey)?.components?.Table?.headerBg;
    if (headerBg) {
        return headerBg;
    }
    return token?.colorPrimary ?? (getCssThemeMode(themeKey) === 'dark' ? '#1f1f1f' : '#f0f0f0');
};

/** Màu chữ header Table theo theme — khớp components.Table.colorTextHeading */
export const getThemeTableHeaderTextColor = (themeKey, token) => {
    const colorTextHeading = resolveThemeConfig(themeKey)?.components?.Table?.colorTextHeading;
    if (colorTextHeading) {
        return colorTextHeading;
    }
    return getCssThemeMode(themeKey) === 'dark' ? token?.colorText : token?.colorTextHeading;
};

/** CSS variables cho master file table (@tanstack/react-table) */
export const getMfTableThemeVars = (themeKey, token) => ({
    '--mf-table-header-bg': getThemeTableHeaderBg(themeKey, token),
    '--mf-table-header-color': getThemeTableHeaderTextColor(themeKey, token),
});

/** Accent top bar navigate panel trên dark (không dùng colorPrimary #1b2531) */
const DARK_NAVIGATE_ACCENT = '#0052AD';

/** CSS vars cho Space.Compact / ant-merge-field — combobox giữ viền; nút + không viền */
export const getMergeFieldCssVars = (themeKey, token = {}) => {
    const isDark = getCssThemeMode(themeKey) === 'dark';
    const hoverBorderColor = isDark
        ? DARK_NAVIGATE_ACCENT
        : token.colorPrimaryBorder ?? token.colorPrimary;

    return {
        '--nagaco-merge-field-border-color': token.colorBorder,
        '--nagaco-merge-field-button-border-color': 'transparent',
        '--nagaco-compact-divider-color': 'transparent',
        '--nagaco-merge-field-hover-border': hoverBorderColor,
    };
};

/** Bảng màu panel navigate (Tổng quát / Tiến trình) — theo theme, khớp layout body */
export const getNavigatePanelColors = (themeKey, token) => {
    const isLight = getCssThemeMode(themeKey) === 'light';
    const layoutBg = getThemeLayoutBodyBg(themeKey, token);
    const cardPanelBg = resolveThemeConfig(themeKey)?.components?.Card?.colorBgContainer;
    const outerBg = isLight ? layoutBg : cardPanelBg ?? token?.colorBgElevated ?? layoutBg;

    return {
        outerBg,
        titleColor: isLight
            ? token?.colorPrimary
            : token?.colorTextHeading ?? token?.menuGroupTitleColor ?? token?.colorText,
        iconColor: isLight
            ? token?.colorPrimary
            : token?.customMenuItemSelectedIconColor ?? DARK_NAVIGATE_ACCENT,
        accentTopColor: isLight ? token?.colorPrimary : DARK_NAVIGATE_ACCENT,
        panelBg: cardPanelBg ?? token?.colorBgElevated ?? token?.colorBgContainer ?? '#ffffff',
        labelColor: isLight ? '#4a4a4a' : token?.colorText,
        valueColor: isLight ? '#757575' : token?.colorTextSecondary ?? token?.colorText,
        dividerColor: isLight ? '#f0f0f0' : token?.colorBorderSecondary,
        borderColor: isLight ? '#d9e6ff' : token?.colorBorder,
        stepWaitBg: outerBg,
    };
};

/** SearchInput giữ style light theme trên header màu theo mùa (Noel, Tết, …) */
export const getSearchInputMode = (themeKey, navMode) => {
    if (themeKey === 'light' || themeKey === 'dark') {
        return navMode;
    }
    return 'dark';
};

export const resolveThemeConfig = (themeKey) => THEMES[themeKey] ?? THEMES.light;

export { getThemeLogoSrc, getThemeLogoExtras } from './themeLogo';
export {
    getFormCardActionButtonStyle,
    getFormCardActionButtonCssVars,
    getPageActionButtonStyle,
    getPageActionButtonCssVars,
    getDetailThemeActionButtonStyle,
    getDetailThemeActionCssVars,
} from './detailThemeActionButton';

/** Scoped CSS cho header @tanstack/react-table — khớp components.Table của từng theme */
export const getMfTableHeaderScopedStyles = (themeKey) => {
    const config = resolveThemeConfig(themeKey);
    const token = config?.token ?? {};
    const headerBg = getThemeTableHeaderBg(themeKey, token);
    const headerColor = getThemeTableHeaderTextColor(themeKey, token);

    return `
.table-master_file,
.table-master_file_approval,
.table-wrapper .table-master_file,
.table-wrapper .table-master_file_approval,
#dynamicTable {
    --mf-table-header-bg: ${headerBg};
    --mf-table-header-color: ${headerColor};
}
.table-master_file thead th,
.table-master_file thead tr,
.table-master_file_approval thead th,
.table-master_file_approval thead tr,
#dynamicTable thead tr,
#dynamicTable th {
    background-color: ${headerBg} !important;
    color: ${headerColor} !important;
}
`;
};

export const getThemeScopedStyles = (themeKey) => {
    const mfTableHeaderStyles = getMfTableHeaderScopedStyles(themeKey);
    const themeConfig = resolveThemeConfig(themeKey);
    const primaryColor =
        themeConfig?.token?.colorPrimary ?? lightTheme.token.colorPrimary;
    const segmentedScopedStyles = buildThemeSegmentedScopedStyles(primaryColor);

    switch (themeKey) {
        case 'light':
            return `${lightScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'dark':
            return `${darkScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'nagaco':
            return `${lightScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'nationalDay':
            return `${nationalDayScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'tet':
            return `${tetScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'womenDay':
            return `${womenDayScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'noel':
            return `${noelScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'midAutumn':
            return `${midAutumnScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'liberation':
            return `${liberationScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        case 'cultureDay':
            return `${cultureDayScopedStyles}${mfTableHeaderStyles}${segmentedScopedStyles}`;
        default:
            return `${mfTableHeaderStyles}${segmentedScopedStyles}`;
    }
};